import { toPng } from "html-to-image";
import html2canvas from "html2canvas";
import { cleanupTransformerAndSelections } from "./canvasPageUtils";
import { navigateBack } from "./navigationBoundsUtils";
import { prepareAndSaveCanvasData as prepareAndSaveCanvasDataUtil } from "./saveUtils";
import { openCanvasDB } from "../../../util/indexedDBUtils";
import { hasCanvasArraysChangedSimple } from "./arrayComparisonUtilsSimple";
import {
  hasActualCanvasChanges,
  createCanvasStateSnapshot,
} from "./canvasDataComparison";

/**
 * Merge live Konva text Group positions into React text state before IndexedDB save.
 * After drag, the stage often reflects the new x/y before the next render flushes `texts`,
 * so persisting props alone can save stale coordinates.
 *
 * @param {Object | null | undefined} mainStage - Konva Stage instance
 * @param {Array} texts
 * @returns {Array}
 */
export const mergeTextPositionsFromStage = (mainStage, texts) => {
  if (!mainStage || !Array.isArray(texts) || texts.length === 0) {
    return texts;
  }

  const findTextGroupNode = (textId) => {
    if (textId == null || textId === "") return null;
    const idStr = String(textId);
    const byIdFn = mainStage.findOne(
      (node) => typeof node.id === "function" && node.id() === idStr,
    );
    if (byIdFn) return byIdFn;
    try {
      return mainStage.findOne(`#${idStr}`);
    } catch {
      return null;
    }
  };

  return texts.map((t) => {
    if (!t) return t;
    const tid = t.id || t.shape_id;
    const node = findTextGroupNode(tid);
    if (!node || typeof node.x !== "function") return t;

    const nx = node.x();
    const ny = node.y();
    let rotation =
      typeof node.rotation === "function" ? node.rotation() : (t.rotation ?? 0);
    rotation = rotation % 360;
    if (rotation < 0) rotation += 360;
    const scaleX =
      typeof node.scaleX === "function" ? node.scaleX() : (t.scaleX ?? 1);
    const scaleY =
      typeof node.scaleY === "function" ? node.scaleY() : (t.scaleY ?? 1);

    const width =
      t.width ?? t.canvas_page_location_and_size?.width;
    const height =
      t.height ?? t.canvas_page_location_and_size?.height;
    const loc = t.canvas_page_location_and_size;

    const next = {
      ...t,
      x: nx,
      y: ny,
      rotation,
      scaleX,
      scaleY,
    };

    if (loc) {
      next.canvas_page_location_and_size = {
        ...loc,
        x: nx,
        y: ny,
        ...(width != null ? { width } : {}),
        ...(height != null ? { height } : {}),
        rotation,
      };
    } else if (width != null && height != null) {
      next.canvas_page_location_and_size = {
        x: nx,
        y: ny,
        width,
        height,
        rotation,
      };
    }

    return next;
  });
};

/**
 * Handles leaving the canvas - generates snapshot and saves state
 * @param {Object} params - Parameters object
 * @param {Object} params.pageId - Current page ID
 * @param {boolean} params.isLeavingCanvas - Flag indicating if we're currently leaving
 * @param {Function} params.setIsLeavingCanvas - Setter for isLeavingCanvas state
 * @param {Function} params.setSnapshot - Setter for snapshot state
 * @param {Object} params.stageRef - Ref to the Konva stage
 * @param {Object} params.transformerRef - Ref to the transformer
 * @param {Function} params.setSelectedTextId - Setter for selected text ID
 * @param {Function} params.setSelectedArrow - Setter for selected arrow
 * @param {Function} params.setSelectedObject - Setter for selected object
 * @param {Function} params.setShowTextBox - Setter for show text box flag
 * @param {Function} params.setSelectedImage - Setter for selected image
 * @param {Function} params.setSelectedObjects - Setter for selected objects array
 * @param {Function} params.setAction - Setter for action state
 * @param {string} params.ACTIONS_SELECT - ACTIONS.SELECT constant
 * @param {boolean} params.fromCanvasTemplate - Whether this is from a canvas template
 * @param {Array} params.texts - Text elements array
 * @param {Array} params.images - Image elements array
 * @param {Array} params.rectangles - Rectangle elements array
 * @param {Array} params.circles - Circle elements array
 * @param {Array} params.arrows - Arrow elements array
 * @param {Array} params.lines - Line elements array
 * @param {Function} params.prepareAndSaveCanvasData - Function to prepare and save canvas data
 * @param {Function} params.navigate - React Router navigate function
 * @param {Object} params.location - React Router location object
 * @param {Object} params.removedShapeIds - Object containing removed shape IDs
 * @param {Object} params.initialArraysState - Initial state of arrays after loadCanvasElements (for change detection)
 * @param {Array} params.scribbles - Scribbles array (for change detection)
 * @param {Array} params.doubleSidedArrows - Double sided arrows array (for change detection)
 * @param {boolean} params.hasUserMadeChanges - Flag indicating if user made changes (set by useEffect watching arrays)
 * @param {string} params.canvasHeight - Current canvas height for change detection
 * @param {Object} params.topLayerStageRef - Ref to the top-layer Konva stage (for compositing when has top-layer images)
 * @param {Function} params.openCanvasDB - Function to open canvas IndexedDB (for navigateBack)
 */
export const handleLeaveCanvas = async ({
  pageId,
  isLeavingCanvas,
  setIsLeavingCanvas,
  setSnapshot,
  stageRef,
  topLayerStageRef,
  transformerRef,
  setSelectedTextId,
  setSelectedArrow,
  setSelectedObject,
  setShowTextBox,
  setSelectedImage,
  setSelectedObjects,
  setAction,
  ACTIONS_SELECT,
  fromCanvasTemplate,
  texts,
  images,
  rectangles,
  circles,
  arrows,
  lines,
  scribbles = [],
  doubleSidedArrows = [],
  prepareAndSaveCanvasData,
  navigate,
  location,
  removedShapeIds = {},
  initialArraysState = null,
  userMadeChanges = false,
  canvasHeight,
  openCanvasDB,
}) => {
  // Prevent multiple simultaneous executions
  if (isLeavingCanvas) {
    return;
  }

  let navigationTriggered = false;

  try {
    if (!pageId) {
      throw new Error("Missing pageId for saving canvas data.");
    }
    setIsLeavingCanvas(true);

    // Clean up transformer and selections
    cleanupTransformerAndSelections(
      transformerRef,
      setSelectedTextId,
      setSelectedArrow,
      setSelectedObject,
      setShowTextBox,
      setSelectedImage,
      setSelectedObjects,
      setAction,
      ACTIONS_SELECT,
    );

    // Generate snapshot
    // For Canvas Template flow, use Konva's native toDataURL as primary method for better reliability
    let snapshot;

    // CRITICAL: Force a full stage redraw to ensure all elements are rendered
    // This is especially important after returning to canvas, as elements might be in a stale state
    if (stageRef.current) {
      stageRef.current.batchDraw();
      // Force a synchronous redraw by accessing the stage
      stageRef.current.draw();
      // Wait a bit for the draw to complete
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    // Count actual elements on stage for debugging
    if (stageRef.current) {
      const allShapes = stageRef.current.find(".shape");
      const allTexts = stageRef.current.find("Text");
      const allRects = stageRef.current.find("Rect");
      const allCircles = stageRef.current.find("Circle");
      const allArrows = stageRef.current.find("Arrow");
      const allImages = stageRef.current.find("Image");

      // Check visibility of elements
      const visibleTexts = allTexts.filter((node) => node.visible() !== false);
      const visibleRects = allRects.filter((node) => node.visible() !== false);
      const visibleCircles = allCircles.filter(
        (node) => node.visible() !== false,
      );
      const visibleArrows = allArrows.filter(
        (node) => node.visible() !== false,
      );
      const visibleImages = allImages.filter(
        (node) => node.visible() !== false,
      );

      // Warn if elements in state don't match elements on stage
      if ((texts?.length || 0) !== allTexts.length) {
        console.warn(
          `⚠️ Text count mismatch: ${texts?.length || 0} in state vs ${
            allTexts.length
          } on stage`,
        );
      }
      // Exclude bg and TextElement white Rects (-whiteRect) when comparing to state
      const rectsCountOnStage = Array.from(allRects || []).filter((n) => {
        const id = n?.getAttr?.("id") ?? n?.attrs?.id ?? "";
        return (
          id !== "bg" && (typeof id !== "string" || !id.endsWith("-whiteRect"))
        );
      }).length;
      if ((rectangles?.length || 0) !== rectsCountOnStage) {
        console.warn(
          `⚠️ Rectangle count mismatch: ${
            rectangles?.length || 0
          } in state vs ${rectsCountOnStage} on stage (excludes bg and text whiteRect). ` +
            `Set localStorage debugCanvasLeaveRectMismatch=1 and leave again to log state vs stage ids.`,
        );
        try {
          if (
            typeof window !== "undefined" &&
            window.localStorage?.getItem("debugCanvasLeaveRectMismatch") ===
              "1"
          ) {
            const stateIds = (rectangles || [])
              .map((r) => r?.id)
              .filter(Boolean);
            const stageIds = Array.from(allRects || [])
              .filter((n) => {
                const id = n?.getAttr?.("id") ?? n?.attrs?.id ?? "";
                return (
                  id !== "bg" &&
                  (typeof id !== "string" || !id.endsWith("-whiteRect"))
                );
              })
              .map((n) => (typeof n.id === "function" ? n.id() : n?.attrs?.id))
              .filter(Boolean);
            console.warn("[CANVAS-LEAVE] Rectangle id diff detail", {
              stateIds,
              stageIds,
              onlyOnStage: stageIds.filter((sid) => !stateIds.includes(sid)),
              onlyInState: stateIds.filter((rid) => !stageIds.includes(rid)),
            });
          }
        } catch (_e) {}
      }
      if ((images?.length || 0) !== allImages.length) {
        console.warn(
          `⚠️ Image count mismatch: ${images?.length || 0} in state vs ${
            allImages.length
          } on stage`,
        );
      }

      // Ensure all elements are visible (some might have been hidden)
      const allElements = [
        ...allTexts,
        ...allRects,
        ...allCircles,
        ...allArrows,
        ...allImages,
      ];
      let madeVisible = false;
      allElements.forEach((node) => {
        if (node.visible() === false) {
          console.warn(`⚠️ Element ${node.id()} is hidden, making visible`);
          node.visible(true);
          madeVisible = true;
        }
      });

      // Redraw if we made any elements visible
      if (madeVisible && stageRef.current) {
        stageRef.current.batchDraw();
        stageRef.current.draw();
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    }

    // Hide the resize handle during snapshot
    const resizeHandle = stageRef.current?.findOne(".resize-handle");
    if (resizeHandle) {
      resizeHandle.hide();
    }

    // Hide blue/red control point circles (orbs) during snapshot
    const controlCircles = stageRef.current
      ?.find("Circle")
      ?.filter((node) => ["blue", "red"].includes(node.fill()));
    if (controlCircles && controlCircles.length > 0) {
      controlCircles.forEach((circle) => circle.hide());
    }

    // Redraw after hiding controls and before capturing
    if (stageRef.current) {
      stageRef.current.batchDraw();
      stageRef.current.draw(); // Force immediate redraw
      // Wait for redraw to complete
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    // Wait for React to flush and Konva to paint so newly added elements appear in the snapshot
    await new Promise((resolve) => requestAnimationFrame(resolve));
    await new Promise((resolve) => setTimeout(resolve, 150));

    if (stageRef.current) {
      stageRef.current.batchDraw();
      stageRef.current.draw();
    }

    // Declare variables outside try for use in catch block
    let originalTransform = null;
    let originalLeft = null;

    // Top-layer images are rendered on a separate Konva Stage (overlay), not on the main stageRef.
    // So stageRef.toDataURL() only captures the main stage and misses top-layer images.
    // When any main-canvas image is top-layer, we must use toPng(container) to capture both stages.
    const imageCount = Array.isArray(images) ? images.length : 0;
    const topLayerImageIds = Array.isArray(images)
      ? images
          .filter((img) => img.isOnTopLayer === true)
          .map((img) => img.id || img.image_id)
      : [];
    const hasTopLayerImages = topLayerImageIds.length > 0;

    const hasTopLayerRef = !!topLayerStageRef?.current;
    let usedKonvaExport = false;
    let snapshotSource = "Konva fallback";

    // When we have top-layer images, composite main stage + top-layer stage, then draw HTML overlays on top.
    // Konva-only composite misses HTML (text, rect/circle labels); we must add them after.
    // Simple snapshot only: ignore top-layer compositing to avoid overlay/styling artifacts.
    if (false && hasTopLayerImages && stageRef.current && topLayerStageRef?.current) {
      try {
        const mainStage = stageRef.current;
        const topStage = topLayerStageRef.current;
        mainStage.batchDraw();
        mainStage.draw();
        topStage.batchDraw();
        topStage.draw();
        const pixelRatio = 2;
        const mainDataUrl = mainStage.toDataURL({
          pixelRatio,
          quality: 1.0,
          mimeType: "image/png",
        });
        const topDataUrl = topStage.toDataURL({
          pixelRatio,
          quality: 1.0,
          mimeType: "image/png",
        });
        const w = mainStage.width() * pixelRatio;
        const h = mainStage.height() * pixelRatio;
        const compositeCanvas = await new Promise((resolve, reject) => {
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          const imgMain = new Image();
          imgMain.onload = () => {
            ctx.drawImage(imgMain, 0, 0);
            const imgTop = new Image();
            imgTop.onload = () => {
              ctx.drawImage(imgTop, 0, 0);
              resolve(canvas);
            };
            imgTop.onerror = () =>
              reject(new Error("Top layer image load failed"));
            imgTop.src = topDataUrl;
          };
          imgMain.onerror = () =>
            reject(new Error("Main stage image load failed"));
          imgMain.src = mainDataUrl;
        });

        // Draw HTML overlays (text, rectangle/circle labels) on top of Konva composite
        const konvaContainer = mainStage.container();
        const htmlWrapperDivs = konvaContainer
          ? Array.from(konvaContainer.children).filter(
              (el) =>
                el.tagName === "DIV" &&
                !el.classList.contains("konvajs-content"),
            )
          : [];
        if (htmlWrapperDivs.length > 0) {
          const ctx = compositeCanvas.getContext("2d");
          const containerRectStage = konvaContainer.getBoundingClientRect();
          for (const wrapper of htmlWrapperDivs) {
            try {
              const r = wrapper.getBoundingClientRect();
              const x = r.left - containerRectStage.left;
              const y = r.top - containerRectStage.top;
              const ww = Math.max(1, Math.ceil(r.width));
              const hh = Math.max(1, Math.ceil(r.height));
              if (ww < 2 || hh < 2) continue;
              const smallCanvas = await html2canvas(wrapper, {
                scale: pixelRatio,
                backgroundColor: null,
                logging: false,
                useCORS: true,
                allowTaint: true,
              });
              if (smallCanvas.width < 2 || smallCanvas.height < 2) continue;
              const smallDataUrl = smallCanvas.toDataURL("image/png", 0.95);
              await new Promise((resolveImg, rejectImg) => {
                const img = new Image();
                img.onload = () => {
                  ctx.drawImage(
                    img,
                    Math.round(x * pixelRatio),
                    Math.round(y * pixelRatio),
                    smallCanvas.width,
                    smallCanvas.height,
                  );
                  resolveImg();
                };
                img.onerror = rejectImg;
                img.src = smallDataUrl;
              });
            } catch (overlayErr) {
              console.warn(
                "[CANVAS-LEAVE] Top-layer path HTML overlay failed:",
                overlayErr?.message,
              );
            }
          }
        }

        snapshot = compositeCanvas.toDataURL("image/png", 0.95);
        if (snapshot && snapshot.length > 1000) {
          usedKonvaExport = true;
          snapshotSource = "Konva composite (top-layer) + HTML overlays";
        }
      } catch (compositeErr) {
        console.warn(
          "[CANVAS-LEAVE] Composite snapshot failed, will use toPng:",
          compositeErr?.message ?? compositeErr,
        );
      }
    }

    // When no top-layer images, prefer toPng(container) first so HTML content (text, rect labels, etc.) is included in the snapshot.
    // Konva toDataURL() only captures the canvas and misses HTML overlays. Only use Konva-first when we composite top-layer.
    if (!usedKonvaExport) {
      // Keep snapshot capture as-is (no DOM mutation) to match what the user sees.
      let exportElementsRestore = [];
      let exportOriginalStylesRestore = [];
      try {
        // Use toPng to capture HTML text overlays and/or top-layer stage when Konva export wasn't used
        const mainStageContainer = document.getElementById(
          "main-canvas-stage-container",
        );
        if (!mainStageContainer) {
          throw new Error("Main stage container not found");
        }

        const containerHasTopLayerAttr =
          mainStageContainer.getAttribute("data-has-top-layer-images") ===
          "true";
        const canvasCount =
          mainStageContainer.querySelectorAll("canvas").length;
        snapshotSource = "(none)";
        // Check if container has content before capturing
        const containerRect = mainStageContainer.getBoundingClientRect();
        if (containerRect.width === 0 || containerRect.height === 0) {
          console.warn("Container has zero dimensions, using stage fallback");
          throw new Error("Container has zero dimensions");
        }

        // Intentionally do not mutate DOM styles/visibility here.

        // CRITICAL: Ensure stage is fully drawn and visible elements are rendered
        // Wait longer for HTML text overlays and all elements to render completely
        // This is important especially when returning to canvas multiple times
        if (stageRef.current) {
          stageRef.current.batchDraw();
          stageRef.current.draw();
        }

        // Wait for DOM to settle and all elements to be visible
        await new Promise((resolve) => setTimeout(resolve, 500));

        // SIMPLE FIRST: take a direct screenshot of the full canvas container.
        // This captures exactly what the user sees and avoids reconstruction artifacts.
        let parentContainer = null;
        let originalParentPosition = null;
        let originalParentLeft = null;
        let originalParentTop = null;
        let originalParentZIndex = null;
        let stageContainer = null;
        let originalStageBorder = null;
        let originalStageBoxShadow = null;
        try {
          // Ensure HTML overlays (parts/task/note/task highlights) are visible for capture.
          const exportElements = mainStageContainer.querySelectorAll(
            '[data-export="true"]',
          );
          exportElementsRestore = Array.from(exportElements);
          exportOriginalStylesRestore = exportElementsRestore.map((el) => ({
            visibility: el.style.visibility,
            opacity: el.style.opacity,
          }));
          exportElementsRestore.forEach((el) => {
            el.style.visibility = "visible";
            el.style.opacity = "1";
          });

          // Neutralize any stage translation/transform so the entire canvas is captured
          // (without capturing only the currently visible scrolled/translated viewport).
          parentContainer = mainStageContainer.parentElement;

          if (parentContainer) {
            originalParentPosition = parentContainer.style.position;
            originalParentLeft = parentContainer.style.left;
            originalParentTop = parentContainer.style.top;
            originalParentZIndex = parentContainer.style.zIndex;

            parentContainer.style.position = "fixed";
            parentContainer.style.left = "-9999px";
            parentContainer.style.top = "0";
            parentContainer.style.zIndex = "-1";
            void parentContainer.offsetHeight;
          }

          originalTransform = mainStageContainer.style.transform;
          originalLeft = mainStageContainer.style.left;
          mainStageContainer.style.transform = "none";
          mainStageContainer.style.left = "0";
          void mainStageContainer.offsetHeight;

          // Remove stage border/shadow ONLY for the snapshot capture.
          stageContainer = stageRef.current?.container?.() ?? null;
          if (stageContainer) {
            originalStageBorder = stageContainer.style.border;
            originalStageBoxShadow = stageContainer.style.boxShadow;
            stageContainer.style.border = "none";
            stageContainer.style.boxShadow = "none";
          }

          const directContainerPng = await toPng(mainStageContainer, {
            cacheBust: true,
            backgroundColor: "#ffffff",
            pixelRatio: 2,
            quality: 0.95,
            skipFonts: false,
          });
          if (directContainerPng && directContainerPng.length > 1000) {
            snapshot = directContainerPng;
            snapshotSource = "toPng(main stage container - direct)";
          }
        } catch (simpleCaptureErr) {
          console.warn(
            "[CANVAS-LEAVE] Direct container screenshot failed, falling back to composite:",
            simpleCaptureErr?.message ?? simpleCaptureErr,
          );
        } finally {
          // Always restore UI state even if toPng throws.
          exportElementsRestore.forEach((el, i) => {
            const orig = exportOriginalStylesRestore[i];
            if (orig) {
              el.style.visibility = orig.visibility || "";
              el.style.opacity = orig.opacity != null ? orig.opacity : "";
            }
          });
          if (parentContainer) {
            parentContainer.style.position = originalParentPosition || "";
            parentContainer.style.left = originalParentLeft || "";
            parentContainer.style.top = originalParentTop || "";
            parentContainer.style.zIndex = originalParentZIndex || "";
          }
          mainStageContainer.style.transform = originalTransform || "";
          mainStageContainer.style.left = originalLeft || "";

          if (stageContainer) {
            stageContainer.style.border = originalStageBorder || "";
            stageContainer.style.boxShadow = originalStageBoxShadow || "";
          }
        }

        // Simple behavior: if the direct screenshot isn't usable, do not run the
        // complex composite/html2canvas fallback paths (they introduce artifacts).
        // The outer catch will fall back to a plain Konva screenshot.
        if (!snapshot || snapshot.length < 1000) {
          throw new Error(
            "Direct toPng(main stage container) produced blank/small snapshot"
          );
        }

        // Keep these scoped variables defined so the (now unreachable) fallback
        // code paths do not trip eslint `no-undef`.
        const pixelRatio = 2;
        const captureWidth =
          mainStageContainer.offsetWidth || containerRect.width;
        const captureHeight =
          mainStageContainer.offsetHeight || containerRect.height;

        if (!snapshot || snapshot.length < 1000) {
          // Composite: Konva canvas + each HTML overlay captured in-place (no clone), then drawn on top.
          const captureWidth =
            mainStageContainer.offsetWidth || containerRect.width;
          const captureHeight =
            mainStageContainer.offsetHeight || containerRect.height;
          const pixelRatio = 2;
          const stage = stageRef.current;
          const konvaContainer = stage?.container();

        // Debug: log stage container structure
        const containerChildren = konvaContainer
          ? Array.from(konvaContainer.children)
          : [];
        const htmlWrapperDivs = konvaContainer
          ? Array.from(konvaContainer.children).filter(
              (el) =>
                el.tagName === "DIV" &&
                !el.classList.contains("konvajs-content"),
            )
          : [];
        const stageW = stage ? stage.width() : captureWidth;
        const stageH = stage ? stage.height() : captureHeight;

        // Collect all [data-export="true"] elements (content, part, part-note, task, note) so parts/part-notes
        // are included even when wrapper divs are few (e.g. only 2 direct children); capture each element in paint order
        const exportElsForOverlay = Array.from(exportElementsRestore || [])
          .filter((el) => {
            if (!el || !el.getBoundingClientRect) return false;
            if (konvaContainer && !konvaContainer.contains(el)) return false;
            const rect = el.getBoundingClientRect();
            return rect.width >= 1 && rect.height >= 1;
          })
          .sort((a, b) => {
            const ra = a.getBoundingClientRect();
            const rb = b.getBoundingClientRect();
            const dy = ra.top - rb.top;
            return Math.abs(dy) > 0.5 ? dy : ra.left - rb.left;
          });

        if (!snapshot && stage) {
          try {
            const konvaDataUrl = stage.toDataURL({
              pixelRatio,
              quality: 1.0,
              mimeType: "image/png",
            });
            const compW = Math.round(stageW * pixelRatio);
            const compH = Math.round(stageH * pixelRatio);
            const compositeCanvas = document.createElement("canvas");
            compositeCanvas.width = compW;
            compositeCanvas.height = compH;
            const ctx = compositeCanvas.getContext("2d");

            const drawDataUrl = (dataUrl) =>
              new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = () => {
                  ctx.drawImage(img, 0, 0, compW, compH);
                  resolve();
                };
                img.onerror = reject;
                img.src = dataUrl;
              });
            await drawDataUrl(konvaDataUrl);

            const containerRectStage = konvaContainer?.getBoundingClientRect();
            let overlayOk = 0;
            let overlayFail = 0;

            // Prefer capturing each [data-export="true"] element so content, part, part-note, task, note are all included
            if (exportElsForOverlay.length > 0 && containerRectStage) {
              for (let i = 0; i < exportElsForOverlay.length; i++) {
                const el = exportElsForOverlay[i];
                try {
                  const r = el.getBoundingClientRect();
                  const x = r.left - containerRectStage.left;
                  const y = r.top - containerRectStage.top;
                  const w = Math.max(1, Math.ceil(r.width));
                  const h = Math.max(1, Math.ceil(r.height));
                  if (w < 2 || h < 2) continue;
                  const smallCanvas = await html2canvas(el, {
                    scale: pixelRatio,
                    backgroundColor: null,
                    logging: false,
                    useCORS: true,
                    allowTaint: true,
                  });
                  const sw = smallCanvas.width;
                  const sh = smallCanvas.height;
                  if (sw < 2 || sh < 2) continue;
                  let smallDataUrl;
                  try {
                    smallDataUrl = smallCanvas.toDataURL("image/png", 0.95);
                  } catch (e) {
                    overlayFail++;
                    continue;
                  }
                  await new Promise((resolve, reject) => {
                    const img = new Image();
                    img.onload = () => {
                      ctx.drawImage(
                        img,
                        Math.round(x * pixelRatio),
                        Math.round(y * pixelRatio),
                        sw,
                        sh,
                      );
                      overlayOk++;
                      resolve();
                    };
                    img.onerror = () => {
                      overlayFail++;
                      reject(new Error("img load failed"));
                    };
                    img.src = smallDataUrl;
                  });
                } catch (err) {
                  overlayFail++;
                }
              }
            }

            // Fallback: if no export elements were drawn, use wrapper divs (e.g. when structure differs)
            if (
              overlayOk === 0 &&
              htmlWrapperDivs.length > 0 &&
              containerRectStage
            ) {
              for (let i = 0; i < htmlWrapperDivs.length; i++) {
                const wrapper = htmlWrapperDivs[i];
                try {
                  const r = wrapper.getBoundingClientRect();
                  const x = r.left - containerRectStage.left;
                  const y = r.top - containerRectStage.top;
                  const w = Math.max(1, Math.ceil(r.width));
                  const h = Math.max(1, Math.ceil(r.height));
                  if (w < 2 || h < 2) continue;
                  const smallCanvas = await html2canvas(wrapper, {
                    scale: pixelRatio,
                    backgroundColor: null,
                    logging: false,
                    useCORS: true,
                    allowTaint: true,
                  });
                  const sw = smallCanvas.width;
                  const sh = smallCanvas.height;
                  if (sw < 2 || sh < 2) continue;
                  let smallDataUrl;
                  try {
                    smallDataUrl = smallCanvas.toDataURL("image/png", 0.95);
                  } catch (e) {
                    overlayFail++;
                    continue;
                  }
                  await new Promise((resolve, reject) => {
                    const img = new Image();
                    img.onload = () => {
                      ctx.drawImage(
                        img,
                        Math.round(x * pixelRatio),
                        Math.round(y * pixelRatio),
                        sw,
                        sh,
                      );
                      overlayOk++;
                      resolve();
                    };
                    img.onerror = () => {
                      overlayFail++;
                      reject(new Error("img load failed"));
                    };
                    img.src = smallDataUrl;
                  });
                } catch (err) {
                  overlayFail++;
                }
              }
            }

            snapshot = compositeCanvas.toDataURL("image/png", 0.95);
            if (snapshot && snapshot.length > 1000) {
              snapshotSource = "composite (Konva + in-place HTML)";
            } else {
              console.warn(
                "[CANVAS-LEAVE] Composite produced no/invalid snapshot, length:",
                snapshot?.length ?? 0,
              );
            }
          } catch (compositeErr) {
            console.warn(
              "[CANVAS-LEAVE] Composite capture failed:",
              compositeErr?.message ?? compositeErr,
            );
          }
        } else {
          console.warn("[CANVAS-LEAVE] No stage ref, skipping composite");
        }

        } // end composite section

        // Fallback: full-container capture if no export elements or composite failed
        if (!snapshot || snapshot.length < 1000) {
          try {
            const html2canvasCanvas = await html2canvas(mainStageContainer, {
              scale: pixelRatio,
              width: captureWidth,
              height: captureHeight,
              backgroundColor: "#ffffff",
              useCORS: true,
              allowTaint: false,
              logging: false,
            });
            const dataUrl = html2canvasCanvas.toDataURL("image/png", 0.95);
            if (dataUrl && dataUrl.length > 1000) {
              snapshot = dataUrl;
              snapshotSource = "html2canvas(container)";
            }
          } catch (html2canvasErr) {
            console.warn(
              "[CANVAS-LEAVE] html2canvas failed:",
              html2canvasErr?.message ?? html2canvasErr,
            );
          }
        }

        if (!snapshot || snapshot.length < 1000) {
          for (let attempt = 0; attempt < 3; attempt++) {
            try {
              const result = await toPng(mainStageContainer, {
                cacheBust: true,
                backgroundColor: "#ffffff",
                pixelRatio,
                quality: 0.95,
                skipFonts: false,
              });
              if (result && result.length > 1000) {
                snapshot = result;
                snapshotSource = "toPng(html-to-image)";
                break;
              }
            } catch (toPngError) {
              console.warn(
                "[CANVAS-LEAVE] toPng attempt",
                attempt + 1,
                "failed:",
                toPngError?.message,
              );
            }
            if (attempt < 2)
              await new Promise((resolve) => setTimeout(resolve, 200));
          }
        }

        // Restore HTML overlay elements' visibility/opacity
        exportElementsRestore.forEach((el, i) => {
          const orig = exportOriginalStylesRestore[i];
          if (orig) {
            el.style.visibility = orig.visibility || "";
            el.style.opacity = orig.opacity != null ? orig.opacity : "";
          }
        });

        // Final validation
        if (!snapshot || snapshot.length < 1000) {
          console.warn(
            "All toPng attempts produced blank/small snapshot, using stage fallback",
          );
          throw new Error("Snapshot appears to be blank after retries");
        }
      } catch (error) {
        console.error(
          "[CANVAS-LEAVE] toPng failed after retries, using Konva fallback:",
          error?.message ?? error,
        );

        // Restore parent position, export elements, and main container in case of error
        const mainStageContainerErr = document.getElementById(
          "main-canvas-stage-container",
        );
        if (mainStageContainerErr) {
          exportElementsRestore.forEach((el, i) => {
            const orig = exportOriginalStylesRestore[i];
            if (orig) {
              el.style.visibility = orig.visibility || "";
              el.style.opacity = orig.opacity != null ? orig.opacity : "";
            }
          });
        }

        // Fallback to stage screenshot (this won't capture HTML text or top-layer images, but better than nothing)
        if (stageRef.current) {
          snapshotSource = "Konva stage (fallback after error)";
          snapshot = stageRef.current.toDataURL({
            pixelRatio: 2,
            quality: 1.0,
            mimeType: "image/png",
          });
        } else {
          console.error(
            "[CANVAS-LEAVE] Stage ref not available for fallback snapshot",
          );
          snapshot = null;
        }
      }
    } // end if (!usedKonvaExport)

    // CRITICAL: Persist to IndexedDB IMMEDIATELY after snapshot, before any setState.
    // This avoids losing the save if setSnapshot or other updates cause re-render/unmount.
    const textsWithStagePositions = mergeTextPositionsFromStage(
      stageRef?.current,
      texts,
    );

    let hasChanges = false;
    try {
      if (initialArraysState) {
        const currentState = createCanvasStateSnapshot({
          texts: textsWithStagePositions,
          rectangles,
          circles,
          arrows,
          lines,
          doubleSidedArrows,
          scribbles,
          images,
          canvasHeight,
        });
        hasChanges = hasActualCanvasChanges(initialArraysState, currentState);
      } else {
        hasChanges = true;
      }
    } catch (e) {
      hasChanges = true;
    }

    let saveOverrides = null;
    if (stageRef?.current) {
      const mainStage = stageRef.current;
      const getA = (node, key, def) => {
        if (!node) return def;
        if (typeof node.getAttr === "function") return node.getAttr(key) ?? def;
        const v = node[key];
        return typeof v === "function" ? v.call(node) : (v ?? def);
      };
      const rectCollection = mainStage.find("Rect");
      const textIds = new Set(
        (textsWithStagePositions || []).flatMap((t) =>
          [t?.id, t?.shape_id].filter(Boolean),
        ),
      );
      // Exclude bg, TextElement white Rect (-whiteRect), and any Rect whose id matches a text (safety)
      const stageRects = Array.from(rectCollection || []).filter((n) => {
        if (!n || (typeof n.visible === "function" && n.visible() === false))
          return false;
        const id = getA(n, "id", "");
        if (!id || id === "bg") return false;
        if (typeof id === "string" && id.endsWith("-whiteRect")) return false;
        if (textIds.has(id)) return false;
        return true;
      });
      const stateRectCount = rectangles?.length || 0;
      if (stageRects.length > stateRectCount) {
        const rectanglesFromStage = stageRects
          .map((node) => {
            const id = getA(node, "id", null);
            if (!id || id === "bg") return null;
            if (typeof id === "string" && id.endsWith("-whiteRect"))
              return null;
            if (textIds.has(id)) return null;
            return {
              id,
              x: getA(node, "x", 0),
              y: getA(node, "y", 0),
              width: getA(node, "width", 100),
              height: getA(node, "height", 100),
              fill: getA(node, "fill", "#ffffff"),
              stroke: getA(node, "stroke", "#000000"),
              strokeWidth: getA(node, "strokeWidth", 2),
              name: getA(node, "name", "Rectangle"),
            };
          })
          .filter(Boolean);
        if (rectanglesFromStage.length > stateRectCount) {
          saveOverrides = { rectangles: rectanglesFromStage };
        }
      }
    }

    let elementCounts;
    let canvasData;
    let result;
    try {
      result = await prepareAndSaveCanvasData(snapshot, {
        ...(saveOverrides || {}),
        texts: textsWithStagePositions,
      });
      elementCounts = result?.elementCounts;
      canvasData = result?.canvasData;
    } catch (err) {
      console.error(
        "[CANVAS-LEAVE] prepareAndSaveCanvasData failed:",
        err?.message ?? err,
        err,
      );
      throw err;
    }

    // Restore resize handle visibility
    if (resizeHandle) {
      resizeHandle.show();
      stageRef.current.batchDraw();
    }

    setSnapshot(snapshot);
    if (snapshot) {
      localStorage.setItem("canvasSnapshot", snapshot);
    } else {
      console.error(
        "❌ No snapshot generated - canvas may appear blank on return",
      );
    }

    if (!hasChanges) {
      // Keep sessionStorage.hasChanges false when deep comparison said no change (for form indicator).
      try {
        sessionStorage.setItem(
          "canvasStateInfo",
          JSON.stringify({
            hasChanges: false,
            lastModified: new Date().toISOString(),
            snapshot,
            pageId,
            elementCounts,
          }),
        );
      } catch (e) {
        // ignore
      }
    }

    // CRITICAL: Also check for persistent unsaved changes flag (persists across MyCanvas visits)
    // This ensures the flag persists even if user doesn't make changes on subsequent visits
    // Use canvas ID (from localStorage) instead of pageId for the persistent flag key
    const canvasId = localStorage.getItem("currentCanvasId") || pageId; // Fallback to pageId if canvasId not found
    const persistentFlagKey = `canvasHasUnsavedChanges_${canvasId}`;
    const hasPersistentChanges =
      localStorage.getItem(persistentFlagKey) === "true";

    // IMPORTANT: Deep-compare can occasionally miss changes depending on normalization.
    // Treat explicit user-made changes as authoritative.
    const finalHasChanges = hasChanges || !!userMadeChanges;

    // CRITICAL FIX: If we end up with no changes, clear the persistent flag
    // This prevents false positives from stale persistent flags.
    if (!finalHasChanges && hasPersistentChanges) {
      localStorage.removeItem(persistentFlagKey);
    }

    // Set flag to prevent API calls when returning to CanvasForm
    localStorage.setItem("preventApiCall", "true");

    // Navigate with state - pass all required parameters including hasChanges
    navigateBack(
      snapshot,
      { elementCounts, canvasData },
      pageId,
      openCanvasDB,
      removedShapeIds,
      location || {}, // Provide empty object as fallback if location is undefined
      navigate,
      finalHasChanges,
    );
    navigationTriggered = true;
  } catch (err) {
    console.error("Error leaving canvas:", err);
    navigate(-1, { state: { snapshot: null, hasChanges: false } });
    navigationTriggered = true;
  } finally {
    // Only reset the leaving state if navigation was never triggered.
    // When navigation has been triggered, the component will unmount naturally;
    // resetting isLeavingCanvas before the route changes causes a brief flash of MyCanvas.
    if (!navigationTriggered) {
      setIsLeavingCanvas(false);
    }
  }
};
